import React, { useEffect, useRef, useState } from 'react';
import { TemplateBox, TemplateElementId, TemplateLayout } from '../../types';

const PAGE_WIDTH_MM = 210;
const HEADER_HEIGHT_MM = 120;
const GRID_MM = 5;
const PX_PER_MM = 3;
const RULER_MM = 8; // ruler thickness in mm

const ELEMENTS: Record<TemplateElementId, { label: string; color: string; default: TemplateBox }> = {
  logo:    { label: 'Logo',          color: '#0ea5e9', default: { x: 18, y: 20, width: 60, height: 18 } },
  company: { label: 'Société',       color: '#10b981', default: { x: 18, y: 42, width: 110, height: 30 } },
  title:   { label: 'Titre & N°',    color: '#f59e0b', default: { x: 110, y: 20, width: 82, height: 30 } },
  client:  { label: 'Client',        color: '#a855f7', default: { x: 18, y: 78, width: 174, height: 25 } },
};

interface Props {
  value: TemplateLayout | null;
  onChange: (next: TemplateLayout) => void;
}

type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';
type DragMode = { kind: 'move' } | { kind: 'resize'; corner: ResizeCorner };

interface DragState {
  id: TemplateElementId;
  mode: DragMode;
  startX: number;
  startY: number;
  startBox: TemplateBox;
}

const snap = (mm: number) => Math.round(mm / GRID_MM) * GRID_MM;

const clampBox = (box: TemplateBox): TemplateBox => {
  const width = Math.max(GRID_MM * 2, Math.min(box.width, PAGE_WIDTH_MM));
  const height = Math.max(GRID_MM * 2, Math.min(box.height, HEADER_HEIGHT_MM));
  return {
    x: Math.max(0, Math.min(box.x, PAGE_WIDTH_MM - width)),
    y: Math.max(0, Math.min(box.y, HEADER_HEIGHT_MM - height)),
    width,
    height,
  };
};

const buildDefaultLayout = (): TemplateLayout => ({
  version: 1,
  elements: Object.fromEntries(
    (Object.keys(ELEMENTS) as TemplateElementId[]).map((k) => [k, ELEMENTS[k].default]),
  ) as TemplateLayout['elements'],
});

/** Apply a corner drag to a starting box. Returns the post-resize box (pre-clamp). */
function resizeFromCorner(start: TemplateBox, corner: ResizeCorner, dxMm: number, dyMm: number): TemplateBox {
  let { x, y, width, height } = start;
  if (corner === 'se') {
    width = start.width + dxMm;
    height = start.height + dyMm;
  } else if (corner === 'sw') {
    x = start.x + dxMm;
    width = start.width - dxMm;
    height = start.height + dyMm;
  } else if (corner === 'ne') {
    y = start.y + dyMm;
    width = start.width + dxMm;
    height = start.height - dyMm;
  } else if (corner === 'nw') {
    x = start.x + dxMm;
    y = start.y + dyMm;
    width = start.width - dxMm;
    height = start.height - dyMm;
  }
  return { x: snap(x), y: snap(y), width: snap(width), height: snap(height) };
}

const InvoiceLayoutEditor: React.FC<Props> = ({ value, onChange }) => {
  const layout = value && value.elements ? value : buildDefaultLayout();
  const elements = layout.elements;

  const canvasRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [selected, setSelected] = useState<TemplateElementId | null>(null);

  useEffect(() => {
    if (!value) onChange(buildDefaultLayout());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateBox = (id: TemplateElementId, box: TemplateBox) => {
    onChange({
      ...layout,
      elements: { ...layout.elements, [id]: clampBox(box) },
    });
  };

  const onPointerDown = (
    e: React.PointerEvent,
    id: TemplateElementId,
    mode: DragMode,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    const box = elements[id];
    if (!box) return;
    setDrag({ id, mode, startX: e.clientX, startY: e.clientY, startBox: box });
    setSelected(id);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const dxMm = (e.clientX - drag.startX) / PX_PER_MM;
    const dyMm = (e.clientY - drag.startY) / PX_PER_MM;
    if (drag.mode.kind === 'move') {
      updateBox(drag.id, {
        ...drag.startBox,
        x: snap(drag.startBox.x + dxMm),
        y: snap(drag.startBox.y + dyMm),
      });
    } else {
      updateBox(drag.id, resizeFromCorner(drag.startBox, drag.mode.corner, dxMm, dyMm));
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (drag) (e.target as Element).releasePointerCapture?.(e.pointerId);
    setDrag(null);
  };

  const resetElement = (id: TemplateElementId) => updateBox(id, ELEMENTS[id].default);
  const resetAll = () => onChange(buildDefaultLayout());

  const updateField = (id: TemplateElementId, field: keyof TemplateBox, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const cur = elements[id] ?? ELEMENTS[id].default;
    updateBox(id, { ...cur, [field]: n });
  };

  const canvasW = PAGE_WIDTH_MM * PX_PER_MM;
  const canvasH = HEADER_HEIGHT_MM * PX_PER_MM;
  const rulerPx = RULER_MM * PX_PER_MM;

  // Ruler tick marks (every 10mm long, every 5mm short).
  const xTicks = Array.from({ length: PAGE_WIDTH_MM / GRID_MM + 1 }, (_, i) => i * GRID_MM);
  const yTicks = Array.from({ length: HEADER_HEIGHT_MM / GRID_MM + 1 }, (_, i) => i * GRID_MM);

  // Corner handle factory — same visual treatment, different cursor + corner ID.
  const cornerStyle = (corner: ResizeCorner, color: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: 10,
      height: 10,
      backgroundColor: color,
      border: '1px solid white',
      borderRadius: 2,
    };
    if (corner === 'nw') return { ...base, top: -5, left: -5, cursor: 'nwse-resize' };
    if (corner === 'ne') return { ...base, top: -5, right: -5, cursor: 'nesw-resize' };
    if (corner === 'sw') return { ...base, bottom: -5, left: -5, cursor: 'nesw-resize' };
    return { ...base, bottom: -5, right: -5, cursor: 'nwse-resize' };
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          Glissez les blocs pour les déplacer, tirez les coins pour redimensionner, ou
          ajustez les dimensions au mm dans le panneau de droite. La grille s'aligne sur {GRID_MM} mm.
        </p>
        <button type="button" onClick={resetAll} className="btn-secondary text-sm">
          Réinitialiser
        </button>
      </div>

      <div className="flex gap-4 flex-wrap">
        {/* Canvas with rulers */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${rulerPx}px ${canvasW}px`,
            gridTemplateRows: `${rulerPx}px ${canvasH}px`,
          }}
        >
          {/* Corner (empty) */}
          <div style={{ width: rulerPx, height: rulerPx, background: '#f8fafc', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }} />
          {/* Top ruler */}
          <div style={{ position: 'relative', width: canvasW, height: rulerPx, background: '#f8fafc', borderBottom: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {xTicks.map((mm) => {
              const isMajor = mm % 10 === 0;
              return (
                <div key={mm} style={{ position: 'absolute', left: mm * PX_PER_MM, top: 0, bottom: 0, width: 1 }}>
                  <div style={{ position: 'absolute', bottom: 0, width: 1, height: isMajor ? rulerPx * 0.55 : rulerPx * 0.3, background: '#94a3b8' }} />
                  {isMajor && (
                    <div style={{ position: 'absolute', top: 2, left: 3, fontSize: 9, color: '#475569', fontFamily: 'monospace' }}>
                      {mm}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Left ruler */}
          <div style={{ position: 'relative', width: rulerPx, height: canvasH, background: '#f8fafc', borderRight: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {yTicks.map((mm) => {
              const isMajor = mm % 10 === 0;
              return (
                <div key={mm} style={{ position: 'absolute', top: mm * PX_PER_MM, left: 0, right: 0, height: 1 }}>
                  <div style={{ position: 'absolute', right: 0, height: 1, width: isMajor ? rulerPx * 0.55 : rulerPx * 0.3, background: '#94a3b8' }} />
                  {isMajor && (
                    <div style={{ position: 'absolute', top: 1, left: 1, fontSize: 9, color: '#475569', fontFamily: 'monospace' }}>
                      {mm}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Canvas */}
          <div
            ref={canvasRef}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerDown={() => setSelected(null)}
            style={{
              width: canvasW,
              height: canvasH,
              backgroundColor: 'white',
              backgroundImage:
                `linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                 linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)`,
              backgroundSize: `${GRID_MM * PX_PER_MM}px ${GRID_MM * PX_PER_MM}px`,
              border: '1px solid #d1d5db',
              position: 'relative',
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            {(Object.keys(ELEMENTS) as TemplateElementId[]).map((id) => {
              const box = elements[id];
              if (!box) return null;
              const meta = ELEMENTS[id];
              const isSelected = selected === id;
              return (
                <div
                  key={id}
                  onPointerDown={(e) => onPointerDown(e, id, { kind: 'move' })}
                  style={{
                    position: 'absolute',
                    left: box.x * PX_PER_MM,
                    top: box.y * PX_PER_MM,
                    width: box.width * PX_PER_MM,
                    height: box.height * PX_PER_MM,
                    backgroundColor: `${meta.color}22`,
                    border: `2px solid ${meta.color}`,
                    borderRadius: 3,
                    cursor: 'move',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: meta.color,
                    fontSize: 11,
                    fontWeight: 600,
                    outline: isSelected ? '2px solid #0f172a' : 'none',
                    outlineOffset: 2,
                    zIndex: isSelected ? 2 : 1,
                  }}
                >
                  {meta.label}
                  {(['nw', 'ne', 'sw', 'se'] as ResizeCorner[]).map((corner) => (
                    <div
                      key={corner}
                      onPointerDown={(e) => onPointerDown(e, id, { kind: 'resize', corner })}
                      style={cornerStyle(corner, meta.color)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Side panel with editable numeric inputs */}
        <div className="flex-1 min-w-[20rem] space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Éléments (en mm)</h3>
          <div className="space-y-2">
            {(Object.keys(ELEMENTS) as TemplateElementId[]).map((id) => {
              const box = elements[id] ?? ELEMENTS[id].default;
              const meta = ELEMENTS[id];
              const isSelected = selected === id;
              return (
                <div
                  key={id}
                  onClick={() => setSelected(id)}
                  className={`rounded border p-2 cursor-pointer ${
                    isSelected ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: meta.color }} />
                      {meta.label}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); resetElement(id); }}
                      className="text-xs text-gray-500 hover:text-primary-700"
                      title="Réinitialiser"
                    >
                      ↺
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-1">
                    {(['x', 'y', 'width', 'height'] as (keyof TemplateBox)[]).map((field) => (
                      <label key={field} className="block">
                        <span className="block text-[10px] uppercase text-gray-500 mb-0.5">
                          {field === 'width' ? 'l' : field === 'height' ? 'h' : field}
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={GRID_MM}
                          value={box[field]}
                          onChange={(e) => updateField(id, field, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full text-xs font-mono px-1 py-0.5 border border-gray-300 rounded focus:outline-none focus:border-gray-900"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500">
            Le reste de la facture (tableau de lignes, totaux, conditions, pied de
            page) reste en flux normal sous la zone d'en-tête de {HEADER_HEIGHT_MM} mm.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceLayoutEditor;
